import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { studentApi } from '../../services/api';
import BookCard from '../../components/BookCard';
import { subjectGroupKey, uniqueSubjectChoices } from '../../utils/subjectOptions';

const F = { fontFamily: 'Inter' } as const;

const StudentLibrary: React.FC = () => {
  const { state } = useLocation() as { state?: { subjectFilter?: { id: number; name: string } } };
  const [books, setBooks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // If the top-bar search sent us here with a subject prefilter, honour it
  // on first render so the user lands on a pre-filtered view.
  const [subjectFilter, setSubjectFilter] = useState(state?.subjectFilter?.name ? subjectGroupKey(state.subjectFilter.name) : '');
  // (holds the ders KEY from subjectGroupKey — same ders under another spelling is one choice)
  // Kitap Ekle modal — student types in the printed activation code from
  // their physical book. Hits POST /student/library/redeem.
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [redeemCode, setRedeemCode] = useState('');
  const [redeemSubmitting, setRedeemSubmitting] = useState(false);
  const [redeemError, setRedeemError] = useState<string | null>(null);
  const [redeemSuccess, setRedeemSuccess] = useState<string | null>(null);

  const reloadLibrary = () => {
    setLoading(true);
    studentApi.library()
      .then((res) => setBooks(res.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reloadLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitRedeem = async () => {
    setRedeemError(null); setRedeemSuccess(null);
    const code = redeemCode.trim().toUpperCase();
    if (!code) { setRedeemError('Lütfen kodunuzu girin.'); return; }
    setRedeemSubmitting(true);
    try {
      const r = await studentApi.redeemBookCode(code);
      setRedeemSuccess(`"${r.data.book.title}" kütüphanenize eklendi.`);
      setRedeemCode('');
      reloadLibrary();
      // Auto-close the modal a beat after success so the user sees the toast
      setTimeout(() => { setRedeemOpen(false); setRedeemSuccess(null); }, 1800);
    } catch (e: any) {
      setRedeemError(e?.response?.data?.error ?? e.message);
    } finally {
      setRedeemSubmitting(false);
    }
  };

  // If a subject preload arrives after mount (rare but possible on back-nav),
  // sync the filter state with it.
  useEffect(() => {
    if (state?.subjectFilter?.name) setSubjectFilter(subjectGroupKey(state.subjectFilter.name));
  }, [state?.subjectFilter?.name]);

  const subjects = uniqueSubjectChoices(books.map((b: any) => b.subject?.name));

  const filtered = subjectFilter
    ? books.filter((b: any) => b.subject?.name && subjectGroupKey(b.subject.name) === subjectFilter)
    : books;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-[20px] lg:text-[24px] font-semibold text-[#2b2b2b]">Kütüphanem</h1>
        <div className="flex items-center gap-4">
          <select
            value={subjectFilter}
            onChange={(e) => setSubjectFilter(e.target.value)}
            className="bg-[#fafafa] rounded-[12px] px-4 py-3 text-[14px] lg:text-[16px] font-medium text-[#2b2b2b] border border-gray-200 outline-none"
          >
            <option value="">Tüm Dersler</option>
            {subjects.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
          <button
            onClick={() => { setRedeemOpen(true); setRedeemError(null); setRedeemSuccess(null); }}
            className="bg-[#e5599b] text-white text-[14px] lg:text-[16px] font-semibold px-5 py-3 rounded-[12px] flex items-center gap-2 hover:bg-pink-600 transition-colors"
          >
            <span className="text-[16px] lg:text-[20px]">+</span> Kitap Ekle
          </button>
        </div>
      </div>

      {redeemOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => !redeemSubmitting && setRedeemOpen(false)} />
          <div className="relative bg-[#f6f6f6] rounded-[24px] w-full max-w-[520px] p-8" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => !redeemSubmitting && setRedeemOpen(false)}
              className="absolute top-5 right-5 w-[44px] h-[44px] rounded-full flex items-center justify-center hover:bg-gray-200"
              aria-label="Kapat"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="3" strokeLinecap="round">
                <line x1="4" y1="4" x2="20" y2="20" /><line x1="20" y1="4" x2="4" y2="20" />
              </svg>
            </button>

            <h2 style={{ ...F, fontSize: 22, fontWeight: 600, color: '#2b2b2b', marginBottom: 8 }}>Kitap Ekle</h2>
            <p style={{ ...F, fontSize: 13, color: '#666', marginBottom: 18 }}>
              Kitabınızın içindeki aktivasyon kodunu girin. Her kod tek seferlik kullanılır.
            </p>

            <label className="block mb-4">
              <span style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>
                Aktivasyon Kodu
              </span>
              <input
                type="text"
                placeholder="PLE-S-XXXXXX-XXXX"
                value={redeemCode}
                onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => { if (e.key === 'Enter' && !redeemSubmitting) submitRedeem(); }}
                disabled={redeemSubmitting}
                autoFocus
                className="w-full bg-white rounded-[12px] px-4 py-3 outline-none text-[16px] font-mono"
                style={{ border: '2px solid #e5599b', letterSpacing: 1, ...F }}
              />
            </label>

            {redeemError && (
              <div className="mb-4 rounded-[10px] px-4 py-2 text-[13px] text-[#c73d74]" style={{ background: '#fdecef' }}>
                {redeemError}
              </div>
            )}
            {redeemSuccess && (
              <div className="mb-4 rounded-[10px] px-4 py-2 text-[13px] text-[#1f7a35]" style={{ background: '#dff5e0' }}>
                {redeemSuccess}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setRedeemOpen(false)}
                disabled={redeemSubmitting}
                className="flex-1 rounded-[12px] py-3 hover:opacity-90 disabled:opacity-50"
                style={{ background: '#e5e5e5' }}
              >
                <span style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b' }}>İptal</span>
              </button>
              <button
                onClick={submitRedeem}
                disabled={redeemSubmitting || !redeemCode.trim()}
                className="flex-1 rounded-[12px] py-3 hover:opacity-90 disabled:opacity-50"
                style={{ background: '#e5599b' }}
              >
                <span style={{ ...F, fontSize: 14, fontWeight: 700, color: '#fff' }}>
                  {redeemSubmitting ? 'Aktive ediliyor…' : 'Kitabı Ekle'}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-center text-gray-400 py-20">Kitap bulunamadı.</p>
      ) : (
        <div className="flex gap-5 flex-wrap">
          {filtered.map((book: any, i: number) => (
            <BookCard key={book.id || i} book={book} index={i} />
          ))}
        </div>
      )}
    </div>
  );
};

export default StudentLibrary;
